import { useTranslate, useTranslation } from 'module/hooks/use-translation';
import { logger } from 'module/logger';
import { GroupedFilter as GroupedFilterType } from 'module/types/filter-field/grouped-filter/filter';
import { isFieldFilterCriteria } from 'module/types/filter-field/grouped-filter/utils';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { PageType } from 'api/page-template/types';
import { useProcessAiQueryMutation } from 'queries/ai-query';
import { FC } from 'react';
import styled from 'styled-components';
import { FilterButton } from 'components/common/styles';
import { AiQueryInput } from './ai-query-input';
import { FilterCriteriaWrapper, Row } from '../components/common';
import { matchTypeLabelAString } from '../components/match-label';
import { getDefaultCriteria } from '../criteria/get-default-criteria';
import { GroupedCriteria } from '../criteria/grouped-criteria';
import { ApiFilterTransformer } from '../filter-transformation/api-filter-transformer';
import { BaseGroupedFilterComponentProps } from '../types';

export interface Props extends BaseGroupedFilterComponentProps {
  hideRemoveButton?: boolean;
  hideMatchTypeText?: boolean;
  addButtonText?: string;
  hasAiQuery?: boolean;
  groupedTransformer?: ApiFilterTransformer<GroupedFilterType>;
  pageType?: PageType;
}

const AddFilterButton = styled(FilterButton)`
  gap: var(--spacing-03);
  border: none;
`;

export const GroupedFilter: FC<Props> = ({
  fields,
  filter,
  hasAiQuery = false,
  onChange,
  groupedTransformer,
  getFilterOperationTypeOptions,
  getSelectEditorComponents,
  hideMatchTypeText = false,
  hideRemoveButton = false,
  addButtonText,
  getOperationOptionRenderer,
  userOptionsGetter,
  pageType,
}) => {
  const default_text = useTranslation('task_list.filters.add');
  const add_filter = addButtonText ?? default_text;
  const translate = useTranslate();

  const { mutateAsync: processAiQuery, isPending: isProcessingAiQuery } =
    useProcessAiQueryMutation();

  const handleAiQuerySubmit = async (aiQuery: string) => {
    if (aiQuery === '' || !groupedTransformer) {
      return;
    }

    try {
      // Transform current filter to API format
      const currentApiFilter = groupedTransformer.to(filter);

      // Call AI query mutation
      const response = await processAiQuery({
        aiQuery,
        groupedCriteria: currentApiFilter,
        pageType: pageType ?? PageType.Email,
      });

      // Transform the response back to GroupedFilter format
      const newGroupedFilter = groupedTransformer.from(response as any);

      // Update the filter with AI-generated criteria
      onChange(newGroupedFilter);
    } catch (error) {
      logger.error('Error processing AI query:', { error: error as Error });
      // You might want to show a toast notification here
    }
  };

  return (
    <>
      <FilterCriteriaWrapper className="rl-advanced-box-wrapper">
        {filter.groupCriteria.map((f, i) =>
          f.hidden ? null : (
            <Row
              key={`${isFieldFilterCriteria(f.criteria) ? f.criteria?.field?.fieldId : f.criteria?.id}_${String(i)}`}
            >
              <GroupedCriteria
                hideMatchTypeText={hideMatchTypeText}
                onRemove={() =>
                  onChange({
                    ...filter,
                    groupCriteria: filter.groupCriteria.filter(
                      (_, j) => i !== j,
                    ),
                  })
                }
                matchTypeLabel={matchTypeLabelAString(
                  i,
                  filter.type,
                  translate,
                )}
                fields={fields}
                filter={f}
                getFilterOperationTypeOptions={getFilterOperationTypeOptions}
                getSelectEditorComponents={getSelectEditorComponents}
                onChange={(g) =>
                  onChange({
                    ...filter,
                    groupCriteria: filter.groupCriteria.map((x, j) =>
                      i !== j ? x : g,
                    ),
                  })
                }
                getOperationOptionRenderer={getOperationOptionRenderer}
                userOptionsGetter={userOptionsGetter}
              />
            </Row>
          ),
        )}

        {filter.groupCriteria.filter((f) => !f?.hidden).length === 0 &&
          !hasAiQuery && (
            <Row>
              <GroupedCriteria
                hideRemoveButton={hideRemoveButton}
                hideMatchTypeText={hideMatchTypeText}
                matchTypeLabel={matchTypeLabelAString(
                  filter.groupCriteria.length,
                  filter.type,
                  translate,
                )}
                fields={fields}
                getFilterOperationTypeOptions={getFilterOperationTypeOptions}
                getSelectEditorComponents={getSelectEditorComponents}
                getOperationOptionRenderer={getOperationOptionRenderer}
                onChange={(g) =>
                  onChange({
                    ...filter,
                    groupCriteria: filter.groupCriteria.concat(g),
                  })
                }
                userOptionsGetter={userOptionsGetter}
              />
            </Row>
          )}
      </FilterCriteriaWrapper>
      {(filter.groupCriteria.filter((f) => !f?.hidden).length > 0 ||
        (hasAiQuery &&
          filter.groupCriteria.filter((f) => !f?.hidden).length === 0)) && (
        <Row>
          <AddFilterButton
            type="text"
            ghost
            data-cy="add.filter"
            onClick={() =>
              onChange({
                ...filter,
                groupCriteria: filter.groupCriteria.concat({
                  id: filter.groupCriteria[0]?.id,
                  criteria: getDefaultCriteria(fields[0].options[0]),
                }),
              })
            }
          >
            <FontAwesomeIcon icon={faPlus} />
            {add_filter}
          </AddFilterButton>
        </Row>
      )}
      {hasAiQuery && (
        <Row>
          <AiQueryInput
            onSubmit={handleAiQuerySubmit}
            isProcessing={isProcessingAiQuery}
          />
        </Row>
      )}
    </>
  );
};
